import React, { useCallback, useEffect, useMemo, useState } from 'react'

import dayjs from 'dayjs'
import duration, { Duration } from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import useSWR from 'swr'

import { Avatar, Button, Logo } from '../atoms'
import { useJobs } from '../hooks/useJobs'

import { RunCardProps, RunTabs } from './RunCard.types'

import { checkPollinationPanel, recipeLinkFromSource, useRuns, useWindowDimensions } from '../hooks'

import './Card.scss'

import { CloudJob, Job, Run, RunStatusEnum } from '@pollination-solutions/pollination-sdk'
import { FolderOutlined, InfoCircleOutlined, 
  LoadingOutlined, PartitionOutlined, SettingOutlined } from '@ant-design/icons'
import { Clock, Cpu, InfoCircle, Trash, XOctagon, Recycle } from 'react-bootstrap-icons'
import { usePollinationPanel } from '../hooks/usePollinationPanel'
import Tag from '../atoms/Tag/Tag'
import { Toaster, toast } from 'react-hot-toast'

dayjs.extend(duration)
dayjs.extend(utc)
dayjs.extend(timezone)

const tmz = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone?.toString()

const formatDuration = (duration: Duration): string => {
  return `${duration.days() > 0 ? `${duration.days()} day${duration.days() !== 1 ? 's' : ''} and ` : ''} ${duration.format('HH:mm:ss')}`
}

interface HoverObject {
  study: boolean
  account: boolean
  project: boolean

  // Click
  details: boolean
  workspace: boolean
  debug: boolean
  // Over
  detailsOver: boolean
  workspaceOver: boolean
  debugOver: boolean

  author: boolean
  recipe: boolean
  info: boolean
  settings: boolean
}

export const RunCard: React.FC<RunCardProps> = ({
  projectName,
  projectOwner,
  run: _run,
  authUser,
  client,
  enableClick = true,
  style = {},
  getTab = (tab: any) => undefined,
  defaultTab = RunTabs.details,
  getValue = (run: Run) => undefined,
  interval = 5000,
  canWrite = false,
  localRun = false,
  loaderNode = undefined
}) => {
  const { fetchLocalRun, fetchLocalJob, fileExplorer, deleteJob } = usePollinationPanel()

  const disabled = useMemo(() => {
    if (!localRun) return

    return !checkPollinationPanel()    
  }, [localRun])

  /*
   * Fetch run
   */
  const { fetchJob } = useJobs(client)
  const { fetchRun, statusMap, getDuration, cancelRun, retryRun } = useRuns(client)
  const { width } = useWindowDimensions()

  const [seeDescription, setSeeDescription] = useState(false)
  const [seeAction, setSeeAction] = useState(false)

  const [stopRefresh, setStopRefresh] = useState<boolean>(false)
  const [isReady, setIsReady] = useState<boolean>(false)

  const [retryLoading, setRetryLoading] = useState<boolean>(false)

  const swrKey = useMemo(() => {
    return authUser && _run ? (!localRun ? [projectOwner, projectName, _run.id] : [_run.id, _run['subfolder']]) : undefined
  }, [authUser, _run, localRun, projectOwner, projectName])

  /*
   * From run and stop when it is done
   */

  const { data: run, error, mutate } = useSWR(
    swrKey,
    localRun ? fetchLocalRun : fetchRun,
    {
      revalidateOnFocus: !stopRefresh,
      refreshWhenHidden: true,
      refreshInterval: stopRefresh ? undefined : interval,
      fallbackData: localRun ? undefined : _run,
      errorRetryCount: 3,
      onSuccess: (run, key, config) => {
        if (localRun) {
          const jobInfo = fetchLocalJob(_run.id, _run['subfolder'])
          setLocalStudy(jobInfo)
        } // It is written at the end of the simulation

        sendRun(run)
        if (run.status.finished_at) setStopRefresh(true)
        setIsReady(true)
      }
    }
  )
  const sendRun = useCallback((run: Run) => getValue(run), [getValue])

  /**
   * Fetch job just one time
   */
  useEffect(() => {
    if (!_run) return
    if (localRun) return

    fetchJob(projectOwner, projectName, run.status.job_id)
      .then(d => {
        setStudy(d)
      })
      .catch(() => { setStudy(undefined) })
  }, [_run, fetchJob, localRun, projectOwner, projectName, run?.status?.job_id])

  /**
   * Refresh status
   */
  const status: string = useMemo(() => {
    if (!run) return undefined
    return run.status.status
  }, [run])

  const [study, setStudy] = useState<CloudJob>()
  const [localStudy, setLocalStudy] = useState<Job | any>()

  const [time, setTime] = useState(new Date())

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date())
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const duration = useMemo(() => {
    if (!run || !time ) return dayjs.duration({ seconds: 0 })
    return getDuration(run)
  }, [run, getDuration, time])
    

  /**
   * Manage hover effect
   */

  const emptyHover: HoverObject = useMemo(() => ({
    study: false,
    account: false,
    project: false,

    details: false,
    workspace: false,
    debug: false,

    detailsOver: false,
    workspaceOver: false,
    debugOver: false,

    author: false,
    recipe: false,
    info: false,
    settings: false,
  }), [])

  const [hover, setHover] = useState<HoverObject>({ ...emptyHover, [defaultTab]: true })
  
  const toggleHover = useCallback((id: string, 
    value = false) => {
    setHover(prevHover => ({
      ...prevHover,
      [id]: value
    })
    )}, [])

  /*
   * Reset hover and use default tab
   */
  useEffect(() => {
    setHover({ ...emptyHover, [defaultTab]: true })
  }, [defaultTab, emptyHover])


  const localStudyURLname = useMemo(() => {
    if (!localStudy) return

    // TODO: improve it in the future, GH defect
    if (!localStudy?.name) return 'Unnamed'

    return localStudy?.name
      ?.replace(/[^a-zA-Z0-9]/g, '')
      ?.replace(/[ ()]/g, '') 
  }, [localStudy])

  /**
   * Rendering - if error
   */
  if (error || !run) return loaderNode ?? <Logo />

  /**
   * Rendering - if CAD by web
   */
  if (disabled) return <div className={enableClick ? `grid-container ${isReady && !run.status.finished_at && 'blink-effect'}` 
    : `grid-container disable-click ${isReady && !run.status.finished_at && 'blink-effect'}`}
  tabIndex={0}
  role='button'
  style={{
    borderColor: statusMap[status]?.color 
        ?? '#d0d7de',
    backgroundColor: '#f5f5f5',
    textAlign: 'center'
  }} 
  >
    <div className='item1'>
      <button className='link' style={{ all: 'unset', cursor: 'pointer', marginRight: '0.75rem' }} >
        <Avatar 
          src={''} 
          size={32} 
          color = {hover.account ? '#40a9ff' : undefined} />
      </button>
      <span style={{ marginRight: '0.75rem' }}>/</span>
      <button className='link' style={{ all : 'unset' }}>
          Enabled in Pollination CAD plugins
      </button>
    </div>
  </div>

  return (
    <div className={(isReady && enableClick) ? `grid-container ${isReady && !run.status.finished_at && 'blink-effect'}` 
      : `grid-container disable-click ${isReady && !run.status.finished_at && 'blink-effect'}`}
    tabIndex={0}
    role='button'
    style={{
      borderColor: statusMap[status]?.color 
          ?? '#d0d7de',
      ...style
    }} 
    onClick={() => { 
      if (!enableClick) return

      if (!localRun) { 
        window.location.href = `/${projectOwner}/projects/${projectName}/studies/${study.id}/runs/${run.id}`
      } else {
        // No studyName. Only completed run are visible in the list
        window.location.href = `/${projectOwner}/projects/${projectName}/localStudies/${localStudyURLname}?path=${_run.id}&subfolder=${_run['subfolder']}`
      }
    }}
    >

      <Toaster />
      {/* Owner section */}
      <div className='item1'>
        <a className='link'
          style={{ all: 'unset', cursor: 'pointer', marginRight: '0.25rem', 
            color: `${hover.account ? '#40a9ff' : '#000'}` }}
          title='Go to account page'
          onMouseOver={ (e) => toggleHover('account', true) }
          onMouseLeave={ (e) => toggleHover('account', false) }
          onClick={(e) => {
            e.stopPropagation()
            if (!run) return

            window.location.href = `/${projectOwner}`
          }}
        >
          {(projectOwner?.length > 20) 
            ? `${projectOwner.substring(0, 20)}...`
            : projectOwner}
        </a>
        <span style={{ marginRight: '0.25rem' }}>/</span>
        <a className='link'
          title='Go to project page'
          onMouseOver={ (e) => toggleHover('project', true) }
          onMouseLeave={ (e) => toggleHover('project', false) }
          onClick={(e) => {
            e.stopPropagation()
            if (!localRun) {
              if (!projectOwner) return

              window.location.href = `/${projectOwner}/projects/${projectName}?tab=studies`
            } else {
              window.location.href = `/${projectOwner}/projects/${projectName}?tab=local+studies`
            }
          }}
          style={hover.project ? { all: 'unset', cursor: 'pointer', color: '#40a9ff' } : { all: 'unset', cursor: 'pointer' }}>
          {projectName ?? '--'}
        </a>
        {localRun && <Tag style={{ display: 'inline', marginLeft: '5px', fontSize: '11px' }}>local</Tag>}
      </div>
      
      {/* Run buttons section */}
      <div className='item2'>
        <div>
          {!localRun && (!isReady || !study) && <LoadingOutlined 
            style={{ margin: '0 10px 0 0' }}/>}
          
          {run &&
            <>
              <span title='Go to run page'>
                <InfoCircleOutlined
                  onClick={(e) => {
                    e.stopPropagation()
                    getTab(RunTabs.details)
                    
                    toggleHover(RunTabs.workspace, false)
                    toggleHover(RunTabs.details, true)
                    toggleHover(RunTabs.debug, false)
                  }}
                  onMouseOver={(e) => {
                    toggleHover('detailsOver', true)
                  }}
                  onMouseLeave={(e) => {
                    toggleHover('detailsOver', false)
                  }}
                  style={{ marginRight: '0.75rem', 
                    color: `${(hover.details || hover.detailsOver) 
                      ? '#40a9ff' : '#000'}` }} />
              </span>
              <span title='Go to workspace page'>
                <FolderOutlined
                  className='link'
                  onClick={(e) => {
                    e.stopPropagation()
                    getTab(RunTabs.workspace)
                    if (localRun) {
                      fileExplorer(_run.id)
                    } else {
                      toggleHover(RunTabs.workspace, true)
                      toggleHover(RunTabs.details, false)
                      toggleHover(RunTabs.debug, false)
                    }
                  }}
                  onMouseOver={(e) => {
                    toggleHover('workspaceOver', true)
                  }}
                  onMouseLeave={(e) => {
                    toggleHover('workspaceOver', false)
                  }}
                  style={{ marginRight: '0.75rem', 
                    color: `${(hover.workspace || hover.workspaceOver)
                      ? '#40a9ff' : '#000'}` }} />
              </span>
              <span title='Go to debug page'>
                <PartitionOutlined
                  className='link'
                  onClick={(e) => {
                    if (!run.status.finished_at) return

                    e.stopPropagation()
                    getTab(RunTabs.debug)
                    if (localRun) {
                      // Get study name
                      const path = !_run['subfolder'] 
                        ? `${_run.id}/${localStudyURLname}/__logs__/err.log`
                        : `${_run.id}/${_run['subfolder']}/${localStudyURLname}/__logs__/err.log`
                      fileExplorer(path)
                    } else {
                      toggleHover(RunTabs.workspace, false)
                      toggleHover(RunTabs.details, false)
                      toggleHover(RunTabs.debug, true)
                    }
                  }}
                  onMouseOver={(e) => {
                    toggleHover('debugOver', true)
                  }}
                  onMouseLeave={(e) => {
                    toggleHover('debugOver', false)
                  }}
                  style={{ marginRight: `${canWrite
                    ? '0.75rem' : '0'}`,  
                  color: `${(!run.status.finished_at) ? 'gray'
                    : (hover.debug || hover.debugOver) ? '#40a9ff' 
                      : '#000'}` }} />
              </span>
            </>}

          {canWrite && <>
            <span title='See study actions'>
              <SettingOutlined
                className='link'
                onMouseOver={(e) => toggleHover('settings', true)}
                onMouseLeave={(e) => toggleHover('settings', false)}
                onClick={(e) => {
                  e.stopPropagation()
                  setSeeAction(prev => !prev)
                  setSeeDescription(false)
                }}
                style={{
                  cursor: 'pointer',
                  color: `${(hover.settings) 
                    ? '#40a9ff' : '#000'}` }} />
            </span>
          </>}
        </div>
      </div>
      
      {/* Study details */}
      <div className='item3'>
        <button style={{ all: 'unset', margin: '0 8px 0 0', cursor: 'pointer' }}
          onMouseOver={(e) => toggleHover('info', true)}
          onMouseLeave={(e) => toggleHover('info', false)}
          onClick={(e) => {
            e.stopPropagation()
            setSeeDescription(prev => !prev)
            setSeeAction(false)
          }}>
          <InfoCircle size={14} style={hover.info ? { color: '#40a9ff' } : {}}/>
        </button>
        <a className='link' 
          title='Go to study page'
          onMouseOver={ (e) => toggleHover('study', true) }
          onMouseLeave={ (e) => toggleHover('study', false) }
          onClick={(e) => {
            e.stopPropagation()
            if (!localRun) {
              if (!projectName || !study) return

              window.location.href = `/${projectOwner}/projects/${projectName}/studies/${study.id}`
            } else {
              // TODO: Local integration
            }
          }}
          style={{ all: 'unset', cursor: 'pointer', color: `${hover.study
            ? '#40a9ff' : '#000'}` }} >
          {localRun ? (
            localStudy ? localStudy.name : _run['studyName'] // Get name from _run
          ) : (
            study ? study.spec.name ?? `Study: ${study.id}` : '--'
          )}
        </a>
      </div>

      {/* Date section */}
      <div className='item4' title={run && run?.status?.finished_at && dayjs(run.status.started_at).utc(localStudy ? false : true).tz(tmz).format('[on] MMM DD YYYY [at] HH:mm')}>
        {run ? dayjs(run.status.started_at).utc(localStudy ? false : true).tz(tmz).format('[on] MMM DD YYYY') : '--'}
      </div>

      {/* Author section */}
      <div className='item5'>
        <span style={{ marginRight: '0.75rem' }}>
          {!localRun && <Avatar 
            color = {hover.author ? '#40a9ff' : undefined} 
            src={run ? run.author?.picture_url : ''} 
            size={24} />}
          
          {localRun && run && <Avatar 
            color = {hover.author ? '#40a9ff' : undefined} 
            src={(localStudy && localStudy?.author) ? localStudy.author?.picture_url : run.author?.picture_url} 
            size={24} />}
        </span>
        <a className='link' 
          onClick={(e) => {
            e.stopPropagation()
            if (!run) return
              
            if (!localRun) {
              window.location.href = `/${run.author.name}`
            } else {
              window.location.href = `/${localStudy?.author?.name}`
            }
          }}
          title='Go to author page'
          rel='noreferrer'
          onMouseOver={ (e) => toggleHover('author', true) }
          onMouseLeave={ (e) => toggleHover('author', false) }
          style={hover.author ? { color: '#40a9ff' } : {}}>
          {!localRun && run && (run.author?.display_name ?? run.author?.name)}
          {localRun && localStudy && (localStudy.author?.display_name ?? run.author.display_name)}
        </a>
      </div>

      {/* Recipe section */}
      <div className='item6'>
        <span style={{ marginRight: '0.75rem' }}>
          <Avatar 
            color = {hover.recipe ? '#40a9ff' : undefined} 
            src={run ? run.recipe?.metadata?.icon : ''} 
            size={24} />
        </span>
        <a className='link'
          onClick={(e) => {
            e.stopPropagation()
            if (!localRun) {
              if (!run) return

              window.location.href = recipeLinkFromSource(run.recipe.source)
            } else {
              // TODO: Local integration
            }
          }}
          title='Go to recipe page'
          rel='noreferrer'
          onMouseOver={ (e) => toggleHover('recipe', true) }
          onMouseLeave={ (e) => toggleHover('recipe', false) }
          style={hover.recipe ? { color: '#40a9ff' } : {}}>
          {run ? run.recipe.metadata.name : '--'}
        </a>
        {run && <span className='light-text'> {run.recipe.metadata.tag}</span>}
      </div>

      {/* Status section */}
      <div className='item8'>
        <span className='status-label' style={{ marginRight: '0.75rem' }}>
          {run ? status : '--'}
        </span>
        <span className='light-text'>
          {!isReady && <LoadingOutlined />}
          {run && width > 600 &&
            `${run.meta.progress.completed} / ${run.meta.progress.total} steps`}
        </span>
      </div>

      {/* CPU section */}
      <div className='item9' title='CPU usage'>
        <Cpu size={18} style={{ marginRight: '0.15rem' }} />
        <span className='light-text'>
          {run ? `${formatDuration(dayjs.duration(run.meta.resources_duration.cpu, 'seconds'))}` : '--'}
        </span>
      </div>
      
      {/* Time section */}
      <div className='item10' title='Duration of the simulation'>
        <Clock size={18} style={{ marginRight: '0.15rem' }} />
        <span className='light-text'>
          {run ? `${formatDuration(duration)}` : '--'}
        </span>
      </div>

      {seeDescription && <>
        <div className='item7'>
          {!localRun && study && study.spec.description}
          {localRun && localStudy && localStudy.description}
        </div>
      </>}

      {seeAction && width && <>
        <div className='item7'
          style={{ display:'flex' }}
        >
          {!localRun && (
            <>
              <Button 
                style={{ marginRight: '0.75rem' }}
                disabled={!run || [RunStatusEnum.Failed, RunStatusEnum.Succeeded, RunStatusEnum.Cancelled, RunStatusEnum.Unknown].includes(run.status.status)}
                onClick={(e) => {
                  e.stopPropagation()
                  if (!run) return

                  toast.promise(
                    cancelRun(projectOwner, projectName, run.id),
                    {
                      loading: 'Cancelling run...',
                      success: (data) => {
                        setStopRefresh(false)
                        return 'Run cancelled'
                      },
                      error: (err) => {
                        console.log(err)
                        return `Failed to cancel run: ${err}`
                      },
                    },
                    {
                      style: { minWidth: '300px', fontSize: '12px' },
                      success: {
                        duration: 5000,
                        icon: '🔥',
                        position: 'bottom-left'
                      },
                      error: {
                        duration: 5000,
                        position: 'bottom-left'
                      },
                      loading: {
                        position: 'bottom-left'
                      }
                    }).finally(() => {
                    mutate()
                  })
                }}
              >
                <XOctagon style={{ display: 'inline', margin: '0 5px 0 0' }} /> Cancel
              </Button>

              <Button 
                style={{ marginRight: '0.75rem' }}
                loading={retryLoading}
                disabled={
                  !run || !run.status ||
                  ![RunStatusEnum.Failed, RunStatusEnum.Cancelled].includes(run.status.status) ||
                  retryLoading
                }
                onClick={(e) => {
                  setRetryLoading(true)
                  e.stopPropagation()
                  if (!run) return

                  toast.promise(
                    retryRun(projectOwner, projectName, run.id),
                    {
                      loading: 'Retrying run...',
                      success: (data) => {
                        setStopRefresh(false)
                        return 'Retry in progress. Please wait.'
                      },
                      error: (err) => {
                        console.log(err)
                        return `Failed to retry run: ${err}`
                      },
                    },
                    {
                      style: { minWidth: '300px', fontSize: '12px' },
                      success: {
                        duration: 5000,
                        icon: '🔥',
                        position: 'bottom-left'
                      },
                      error: {
                        duration: 5000,
                        position: 'bottom-left'
                      },
                      loading: {
                        position: 'bottom-left'
                      }
                    }
                  ).finally(() => {
                    mutate()
                    setRetryLoading(false)
                  })
                }}
              >
                <Recycle style={{ display: 'inline', margin: '0 5px 0 0' }} /> Retry
              </Button>
            </>
          )}

          {localRun && <Button 
            disabled={!run || !run.status.finished_at}
            onClick={(e) => {
              e.stopPropagation()
              if (!run) return

              const count = deleteJob(`${projectOwner}/${projectName}`, _run.id, _run['subfolder'])
              if (count > 0) {
                if (window.location.href.includes('?path=')) {
                  window.history.back()
                } else {
                  window.location.reload()
                }
              }
            }}
          >
            <Trash style={{ display: 'inline', margin: '0 5px 0 0' }} /> Delete
          </Button>}
        </div>
      </>}
    </div>
  )
}

export default RunCard
